import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { Game } from '../../games.model';
import { TileRowCollection } from '../../GameCard/GameCard.types';
import { useFetchGames } from '../../../../hooks/useFetchGames';
import { transformCategoriesGameData } from '../../../../utils/games/games.utils';
import { locale } from '../../../../consts';
import { ViewAllGamesGridSkeleton } from './ViewAllGamesGridSkeleton';
import { ViewAllGamesGridError } from './ViewAllGamesGridError';
import { ViewAllGamesGridContent } from './ViewAllGamesGridContent';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import { createTileRowCollections } from '../../../../utils/games/createTileRowCollections';
import { GameCardContainerType, SectionType } from '../../GameCard/GameCard.types';
import './ViewAllGamesGrid.scss';

interface ViewAllGamesGridProps {
  categoryName: string;
  isVisible: boolean;
  onBackToCarousel: () => void;
  disabled?: boolean;
  showGameName?: boolean;
  fallbackImageAltText?: string;
  fallbackImageUrl?: string;
  onViewGameInfo?: (game: Game) => void;
  onPlayDemo?: (game: Game) => void;
  onPlayGame?: (game: Game) => void;
  containerElementWidth?: number;
  // New props for configuration mode
  displayMode?: 'grid' | 'original';
  originalTileRowCollections?: TileRowCollection[];
  originalGames?: Game[];
  categoryInternalName: string;
  category?: string;
}

export const ViewAllGamesGrid: FC<ViewAllGamesGridProps> = ({
  categoryName,
  isVisible,
  onBackToCarousel,
  disabled = false,
  showGameName = true,
  fallbackImageAltText = '',
  fallbackImageUrl = '',
  onViewGameInfo,
  onPlayDemo,
  onPlayGame,
  containerElementWidth = 0,
  displayMode = 'grid',
  originalTileRowCollections = [],
  categoryInternalName,
}) => {
  const [fetchedGames, setFetchedGames] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasRequestedGames, setHasRequestedGames] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslations();
  const { fetchGamesByCategory } = useFetchGames({
    onGamesGetByCategoryComplete: (data) => {
      try {
        const allGames = transformCategoriesGameData(data.categories);

        setFetchedGames(allGames);
        setError(null);
      } catch (err) {
        setError('Failed to process game data');
        console.error('Failed to transform category games:', err);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const requestGames = useCallback(() => {
    setHasRequestedGames(true);
    setIsLoading(true);
    fetchGamesByCategory({
      variables: { category: categoryInternalName, language: locale, count: 2500 },
    });
  }, [categoryInternalName, fetchGamesByCategory]);

  const handleRetry = useCallback(() => {
    setError(null);
    requestGames();
  }, [requestGames]);

  useEffect(() => {
    if (isVisible && !hasRequestedGames && !error) {
      requestGames();
    }
  }, [isVisible, hasRequestedGames, error, requestGames]);

  useEffect(() => {
    if (!isVisible) {
      setFetchedGames([]);
      setIsLoading(false);
      setError(null);
      setHasRequestedGames(false);
    }
  }, [isVisible]);

  const tileCollections = useMemo(() => {
    if (displayMode === 'original' && originalTileRowCollections.length > 0) {
      // For original mode, create a lobby item structure with all fetched games
      // and use the original tile collection configuration
      const originalTileConfig = originalTileRowCollections[0];
      const lobbyItem = {
        slug: `view-all-${categoryName.toLowerCase().replace(/\s+/g, '-')}`,
        icon: '',
        title: categoryName,
        games: fetchedGames,
        carouselType: GameCardContainerType.Box,
        description: `View all ${categoryName} games`,
        internalName: categoryName,
        tile_row_collection: {
          ...originalTileConfig,
          SeeMoreViewAll: false, // Disable for view-all mode
        },
      };

      return createTileRowCollections([lobbyItem], []);
    }

    // For grid mode, create uniform grid collections from fetched games
    const gridLobbyItem = {
      slug: `grid-${categoryName.toLowerCase().replace(/\s+/g, '-')}`,
      icon: '',
      title: categoryName,
      games: fetchedGames,
      carouselType: GameCardContainerType.Box,
      description: `${categoryName} games`,
      internalName: categoryName,
      tile_row_collection: {
        id: `grid-${categoryName}`,
        internal_name: categoryName,
        SeeMoreViewAll: false,
        Width: 'FULL' as const,
        first_full_width_tile: {
          type: SectionType.GRID,
        },
        second_full_width_tile: {
          type: SectionType.GRID,
        },
        third_full_width_tile_block: {
          type: SectionType.GRID,
        },
      },
    };

    return createTileRowCollections([gridLobbyItem], []);
  }, [displayMode, originalTileRowCollections, fetchedGames, categoryName]);

  const gamesToUse = fetchedGames;

  if (!isVisible) return null;

  if (isLoading) {
    return <ViewAllGamesGridSkeleton categoryName={categoryName} />;
  }

  if (error && displayMode === 'grid') {
    return <ViewAllGamesGridError error={error} categoryName={categoryName} onRetry={handleRetry} />;
  }

  if (hasRequestedGames && !isLoading && gamesToUse.length === 0) {
    const errorMessage =
      displayMode === 'original'
        ? t('game.carousel.error.no-games-carousel')
        : t('game.carousel.error.no-games-category');

    return (
      <ViewAllGamesGridError
        error={errorMessage}
        categoryName={categoryName}
        onRetry={displayMode === 'grid' ? handleRetry : undefined}
      />
    );
  }

  return (
    <ViewAllGamesGridContent
      collections={tileCollections}
      games={gamesToUse}
      categoryName={categoryName}
      disabled={disabled}
      showGameName={showGameName}
      fallbackImageAltText={fallbackImageAltText}
      fallbackImageUrl={fallbackImageUrl}
      onViewGameInfo={onViewGameInfo}
      onPlayDemo={onPlayDemo}
      onPlayGame={onPlayGame}
      containerElementWidth={containerElementWidth}
    />
  );
};
